import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Card,
  CardActions,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { FunctionComponent } from 'react';

import { Dialog } from '../../components/Dialog';
import { Tooltip } from '../../components/Tooltip';
import { Layout } from '../../navigation/components/Layout/Layout';
import { DeleteParticipantDialog } from '../AdminEvents/components/DeleteParticipantDialog/DeleteParticipantDialog';
import { CommentDialog } from './components/CommentDialog/CommentDialog';
import { PaymentDialog } from './components/PaymentDialog/PaymentDialog';
import { SuperLikeDialog } from './components/SuperLikeDialog/SuperLikeDialog';
import { CheckboxFilterType, useAdminEventUsers } from './useAdminEventUsers';

const cautionLabel: Record<CheckboxFilterType, string> = {
  all: 'Tous',
  only: 'Avec caution',
  'only-not': 'Sans caution',
};

const paiementLabel: Record<CheckboxFilterType, string> = {
  all: 'Tous',
  only: 'Payé uniquement',
  'only-not': 'Pas payé uniquement',
};

const validationLabel: Record<CheckboxFilterType, string> = {
  all: 'Tous',
  only: 'Validé',
  'only-not': 'Pas validé',
};

export const AdminEventUsers: FunctionComponent = () => {
  const {
    eventUsers,
    loading,
    onCautionPress,
    dialog,
    onCloseDialog,
    onPaymentPress,
    paymentDialogInformation,
    onClosePaymentDialog,
    onValidatePress,
    onEditOrgaComment,
    commentDialogInformation,
    onCloseCommentDialog,
    superLikeDialogInformation,
    onCloseSuperLikeDialog,
    onEditMaxSuperLikes,
    onCloseDeleteParticipantDialog,
    onDeleteParticipant,
    participantToDeleteId,
    onShowDeleteParticipantDialog,
    onDownloadCsv,
    onPaiementFilterCheckboxChange,
    paiementFilter,
    cautionFilter,
    onCautionFilterCheckboxChange,
    onValidationFilterCheckboxChange,
    validationFilter,
    clearSearch,
    onSearchChange,
    search,
    eventOptions,
    selectedPreviousEventId,
    onSelectedPreviousEventIdChange,
  } = useAdminEventUsers();

  const columns: GridColDef[] = [
    { field: 'firstname', headerName: 'Prénom' },
    { field: 'lastname', headerName: 'Nom' },
    { field: 'studies', headerName: 'Études' },
    { field: 'studyTown', headerName: 'Ville' },
    { field: 'nickname', headerName: 'Surnom' },
    {
      field: 'hasCaution',
      headerName: 'Caution',
      align: 'center',
      headerAlign: 'center',
      width: 70,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.hasCaution}
          onClick={() => onCautionPress(params.row)}
        />
      ),
    },
    {
      field: 'hasPaidTicket',
      headerName: 'Payé',
      align: 'center',
      headerAlign: 'center',
      width: 70,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.hasPaidTicket}
          onClick={() => onPaymentPress(params.row)}
        />
      ),
    },
    {
      field: 'isValidated',
      headerName: 'Validé',
      align: 'center',
      headerAlign: 'center',
      width: 70,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.isValidated}
          onClick={() => onValidatePress(params.row)}
        />
      ),
    },
    {
      field: 'waitingListPosition',
      headerName: "Liste d'Attente",
      width: 110,
    },
    {
      field: 'orgaComment',
      headerName: 'Commentaire Orga',
      width: 250,
      renderCell: (params) => (
        <Box
          width={250}
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
        >
          <Tooltip key={params.row.orgaComment} title={params.row.orgaComment}>
            <Typography width={180} noWrap variant="body2">
              {params.row.orgaComment}
            </Typography>
          </Tooltip>
          <Tooltip title="Modifier le commentaire">
            <IconButton
              color="info"
              onClick={() => onEditOrgaComment(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'userComment',
      headerName: 'Commentaire Utilisateur',
      width: 250,
      renderCell: (params) => (
        <Tooltip key={params.row.userComment} title={params.row.userComment}>
          {params.row.userComment}
        </Tooltip>
      ),
    },
    { field: 'email', headerName: 'Email', width: 250 },
    {
      field: 'maxSuperLikes',
      headerName: 'Super likes',
      width: 100,
      renderCell: (params) => (
        <Box
          width={100}
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
        >
          <Tooltip
            key={params.row.maxSuperLikes}
            title={params.row.maxSuperLikes}
          >
            <Typography noWrap variant="body2">
              {params.row.maxSuperLikes}
            </Typography>
          </Tooltip>
          <Tooltip title="Modifier le nombre de super likes">
            <IconButton
              color="info"
              onClick={() => onEditMaxSuperLikes(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'Actions',
      width: 200,
      renderCell: (params) => (
        <>
          <Tooltip title="Supprimer le participant">
            <IconButton
              color="error"
              onClick={() => onShowDeleteParticipantDialog(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Layout
      title="Administration - Événement - Participants"
      activeTab="adminEventUsers"
    >
      <Card sx={{ mb: 2 }}>
        <CardActions sx={{ justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            onClick={onDownloadCsv}
            disabled={eventUsers.length <= 0}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Télécharger le CSV
          </Button>
        </CardActions>
        <CardActions>
          <Typography>Filtres:</Typography>
        </CardActions>
        <CardActions
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2,
          }}
        >
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1}
            flex={1}
            sx={{
              '& .MuiFormControlLabel-root': {
                m: 0,
                width: { xs: '100%', sm: 'auto' },
              },
            }}
          >
            <FormControlLabel
              label={`Caution (${cautionLabel[cautionFilter]})`}
              control={
                <Checkbox
                  checked={cautionFilter === 'only'}
                  indeterminate={cautionFilter === 'all'}
                  onChange={onCautionFilterCheckboxChange}
                />
              }
            />

            <FormControlLabel
              label={`Paiement (${paiementLabel[paiementFilter]})`}
              control={
                <Checkbox
                  checked={paiementFilter === 'only'}
                  indeterminate={paiementFilter === 'all'}
                  onChange={onPaiementFilterCheckboxChange}
                />
              }
            />

            <FormControlLabel
              label={`Validé (${validationLabel[validationFilter]})`}
              control={
                <Checkbox
                  checked={validationFilter === 'only'}
                  indeterminate={validationFilter === 'all'}
                  onChange={onValidationFilterCheckboxChange}
                />
              }
            />
          </Box>

          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            width={{ xs: '100%', md: 'auto' }}
            sx={{ ml: { md: 'auto' } }}
          >
            <TextField
              select
              label="Événement précédent"
              size="small"
              value={selectedPreviousEventId}
              onChange={onSelectedPreviousEventIdChange}
              fullWidth
              sx={{ width: { xs: '100%', sm: 240 } }}
            >
              <MenuItem value="">Ne pas filtrer</MenuItem>
              {eventOptions.map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  {event.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Rechercher un participant"
              size="small"
              value={search}
              onChange={onSearchChange}
              fullWidth
              sx={{
                width: { xs: '100%', sm: 280, lg: 360 },
                maxWidth: '100%',
              }}
              InputProps={{
                startAdornment: <SearchIcon />,
                endAdornment:
                  search.length > 0 ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={clearSearch}
                        onMouseDown={clearSearch}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
              }}
            />
          </Box>
        </CardActions>
      </Card>
      <DataGrid
        rows={eventUsers}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        loading={loading}
      />
      {dialog ? (
        <Dialog
          title={dialog.title}
          description={dialog.description}
          onSubmit={dialog.onSubmit}
          onCancel={onCloseDialog}
          open
        />
      ) : null}
      {paymentDialogInformation ? (
        <PaymentDialog
          open
          onClose={onClosePaymentDialog}
          userId={paymentDialogInformation.userId}
          eventId={paymentDialogInformation.eventId}
        />
      ) : null}
      {commentDialogInformation ? (
        <CommentDialog
          open
          onClose={onCloseCommentDialog}
          userId={commentDialogInformation.userId}
          eventId={commentDialogInformation.eventId}
          defaultOrgaComment={commentDialogInformation.orgaComment}
        />
      ) : null}
      {superLikeDialogInformation ? (
        <SuperLikeDialog
          open
          onClose={onCloseSuperLikeDialog}
          userId={superLikeDialogInformation.userId}
          eventId={superLikeDialogInformation.eventId}
          maxSuperLikes={superLikeDialogInformation.maxSuperLikes}
        />
      ) : null}
      <DeleteParticipantDialog
        onCancel={onCloseDeleteParticipantDialog}
        onDelete={onDeleteParticipant}
        open={participantToDeleteId !== null}
      />
    </Layout>
  );
};
